/**
 * Streaming Chat Hook
 * 
 * Custom hook for consuming SSE streams from the AI assistant API.
 * Uses typewriter effect for natural reading pace.
 */

import { useState, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../services/api';
import { useTypewriter } from './useTypewriter';

export interface StreamingState {
  /** Whether the network is still receiving data */
  isStreaming: boolean;
  /** Whether the typewriter is still animating */
  isTyping: boolean;
  /** The currently displayed text (typewriter animated) */
  displayedContent: string;
  /** The full received content (may be ahead of displayed) */
  fullContent: string;
  error: string | null;
  suggestedFollowUps: string[];
}

interface SSEData {
  content?: string;
  done?: boolean;
  suggestedFollowUps?: string[];
  error?: string;
}

/**
 * Buffered SSE parser that handles chunk boundaries correctly.
 * Maintains a carry-over buffer so partial lines split across reads
 * are reassembled before parsing.
 */
function createSSEParser() {
  let buffer = '';

  function parse(chunk: string): SSEData[] {
    buffer += chunk;
    const results: SSEData[] = [];

    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      for (const line of part.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            results.push(JSON.parse(line.slice(6)));
          } catch {
            // Malformed JSON within a complete event — skip
          }
        }
      }
    }

    return results;
  }

  /** Flush any remaining buffered data as a final event. */
  function flush(): SSEData[] {
    return parse('\n\n');
  }

  return { parse, flush };
}

export function useStreamingChat() {
  const [state, setState] = useState<Omit<StreamingState, 'displayedContent' | 'isTyping'>>({
    isStreaming: false,
    fullContent: '',
    error: null,
    suggestedFollowUps: [],
  });
  
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Typewriter effect - 55 chars/sec (50% faster than before)
  const { displayedText, isTyping, addText, reset: resetTypewriter, skipToEnd } = useTypewriter({
    speed: 55,
    autoStart: true,
  });

  /**
   * Start streaming a message to the general assistant
   */
  const streamMessage = useCallback(async (
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<string> => {
    return streamToEndpoint(
      `${API_BASE_URL}/assistant/stream`,
      { message, history }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Start streaming a message with incident context
   */
  const streamIncidentMessage = useCallback(async (
    tenantId: string,
    incidentId: string,
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
    context: string
  ): Promise<string> => {
    return streamToEndpoint(
      `${API_BASE_URL}/assistant/incidents/${encodeURIComponent(tenantId)}/${encodeURIComponent(incidentId)}/stream`,
      { message, history, context }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Internal function to stream from an endpoint
   */
  const streamToEndpoint = useCallback(async (
    url: string,
    body: object
  ): Promise<string> => {
    // Cancel any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    resetTypewriter();

    setState({
      isStreaming: true,
      fullContent: '',
      error: null,
      suggestedFollowUps: [],
    });

    let fullContent = '';

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      const sse = createSSEParser();

      const processEvents = (events: SSEData[]) => {
        for (const event of events) {
          if (event.error) {
            throw new Error(event.error);
          }

          if (event.content) {
            fullContent += event.content;
            addText(event.content);
            setState(prev => ({ ...prev, fullContent }));
          }

          if (event.done && event.suggestedFollowUps) {
            setState(prev => ({
              ...prev,
              suggestedFollowUps: event.suggestedFollowUps || [],
            }));
          }
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        processEvents(sse.parse(chunk));
      }

      // Flush the TextDecoder and SSE parser buffer for any trailing data
      const trailing = decoder.decode();
      if (trailing) processEvents(sse.parse(trailing));
      processEvents(sse.flush());

      setState(prev => ({
        ...prev,
        isStreaming: false,
      }));

      return fullContent;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        setState(prev => ({ ...prev, isStreaming: false }));
        return fullContent;
      }

      const errorMessage = error instanceof Error ? error.message : 'Stream failed';
      setState(prev => ({
        ...prev,
        isStreaming: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [addText, resetTypewriter]);

  /**
   * Cancel the current stream
   */
  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState(prev => ({ ...prev, isStreaming: false }));
  }, []);

  /**
   * Reset the streaming state
   */
  const resetStream = useCallback(() => {
    cancelStream();
    resetTypewriter();
    setState({
      isStreaming: false,
      fullContent: '',
      error: null,
      suggestedFollowUps: [],
    });
  }, [cancelStream, resetTypewriter]);

  return {
    ...state,
    displayedContent: displayedText,
    isTyping,
    streamMessage,
    streamIncidentMessage,
    cancelStream,
    resetStream,
    skipToEnd,
  };
}
